import { Injectable } from '@angular/core';
import { GetOptions, Preferences, RemoveOptions, SetOptions } from '@capacitor/preferences';
import { from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoggerService } from './logger/logger.service';
import { NativeCheckService } from './native-check.service';

export const LOCAL_STORAGE_KEYS = {
  REDIRECT_AFTER_LOGIN: 'redirectAfterLogin',
  USER_LOCAL_STORAGE_KEY: 'userData',
  BEARER_STORAGE_KEY: 'bearer',
  TOKEN_LOCAL_STORAGE_KEY: 'refreshToken',
  TOKEN_EXPIRES_LOCAL_STORAGE_KEY: 'refreshTokenExpires',
  LOCAL_STORAGE_TIMER_KEY: 'lastActive',
  COOKIES_ANALYTICS_CATEGORY: 'analytics',
  COOKIE_STORAGE_KEY: 'cc_cookie',
  NATIVE_COOKIE_STORAGE_KEY: 'native_ccookie',
  LAST_ACTIVE_ROUTE: 'last_active_route',
}

/**
 * Created to prevent iOS localStorage unexpected clears.
 * https://forum.ionicframework.com/t/strange-bug-ios-local-storage-is-being-erased/147344/7
 */
@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  public constructor(
    private loggerService: LoggerService,
    private nativeCheckService: NativeCheckService,
  ) { }

  public setItem(data: SetOptions): void {
    if(this.nativeCheckService.isNativePlatform()) {
      Preferences.set(data);
    } else {
      // fallback for browsers that watch over localStorage ex. 'window.addEventListener('storage' ...'
      localStorage.setItem(data.key, data.value);
    }
  }

  /**
   * @returns storage value, ❗empty string is returned if string not set or deleted
   */
  public getItem(data: GetOptions): Observable<string> {
    if(this.nativeCheckService.isNativePlatform()) {
      return from(Preferences.get(data)).pipe(
        map(result => result.value ?? '')
      );

    } else {
      return of(localStorage.getItem(data.key) ?? '');
    }
  }

  /**
   * Do not use on Capacitor builds! For browser only.
   * Ex. for checking between tabs.
   */
  public getItemSync(data: GetOptions): string | null {
    return localStorage.getItem(data.key);
  }

  /**
   * Do not use on Capacitor builds! For browser only.
   */
  public deleteItemSync(data: RemoveOptions): void {
    localStorage.removeItem(data.key);
  }

  public async deleteItem(data: RemoveOptions): Promise<void> {
    try {
      localStorage.removeItem(data.key);
      await Preferences.remove(data);
    } catch (error) {
      this.loggerService.error(error);
    }
  }

  public async clear(): Promise<void> {
    localStorage.clear();
    await Preferences.clear();
  }
}
